import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import { apiError } from "@/api";
import { Hammer } from "lucide-react";

export default function Login() {
  const { login, resendVerification } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needVerify, setNeedVerify] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("auth.loginBtn"));
      const next = params.get("next") || "";
      nav(next.startsWith("/") && !next.startsWith("//") ? next : "/dashboard");
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === "EMAIL_NOT_VERIFIED") {
        setNeedVerify(true);
      } else {
        toast.error(apiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    await resendVerification(email);
    toast.success(t("verify.resent"));
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="inline-flex w-12 h-12 rounded-2xl bg-[#C65338] items-center justify-center mb-4">
            <Hammer className="w-6 h-6 text-white" />
          </span>
          <h1 className="font-display text-3xl font-black tracking-tight text-[#2D231F]">{t("auth.loginTitle")}</h1>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#EBDDD3] p-8 space-y-5">
          {needVerify && (
            <div className="rounded-lg bg-[#E69625]/10 border border-[#E69625]/30 px-3 py-3 text-sm text-[#B27A1E]" data-testid="login-verify-notice">
              <p className="mb-2">{t("verify.needVerify")}</p>
              <button type="button" data-testid="login-resend" onClick={resend} className="font-semibold underline">{t("verify.resend")}</button>
            </div>
          )}
          <div className="space-y-2">
            <Label>{t("auth.email")}</Label>
            <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>{t("auth.password")}</Label>
            <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-lg" />
            <div className="text-end">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm text-[#C65338] font-semibold hover:underline">{t("auth.forgotPassword")}</Link>
            </div>
          </div>
          <Button data-testid="login-submit" disabled={loading} type="submit" className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-semibold">
            {t("auth.loginBtn")}
          </Button>
          <p className="text-sm text-center text-[#8C7A70]">
            {t("auth.noAccount")} <Link to="/register" className="text-[#C65338] font-semibold">{t("nav.register")}</Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
}